import { IconSparkles } from "@tabler/icons-react"
import { type FC, useEffect, useMemo, useState } from "react"

import type { WebsiteEditProgress } from "@forento/core/models/aiWebsite"

const STAGE_MESSAGES: Record<WebsiteEditProgress["stage"], string[]> = {
	reading: ["Loading your pages and sections", "Looking at how your site is put together"],
	planning: [
		"Matching your request to the site",
		"Deciding which sections to touch",
		"Leaving unrelated sections untouched",
	],
	writing: [
		"Writing in your site's voice",
		"Working on several sections at once",
		"Drafting the new copy",
		"Keeping your images and links in place",
	],
	finalizing: ["Assembling the before and after", "Preparing the proposal for review"],
}

const ROTATION_INTERVAL_MS = 2_800
const FADE_DURATION_MS = 300

function getStageLabel(progress: WebsiteEditProgress): string {
	switch (progress.stage) {
		case "reading":
			return "Reading your website"
		case "planning":
			return "Planning the changes"
		case "writing":
			return `Writing the new copy (${Math.min(progress.completedBlocks + 1, progress.totalBlocks)} of ${progress.totalBlocks})`
		case "finalizing":
			return "Putting the proposal together"
	}
}

type Props = { progress: WebsiteEditProgress | null }

const EditProgress: FC<Props> = ({ progress }) => {
	const currentProgress = progress ?? { stage: "reading" as const }

	return (
		<div className="flex max-w-[80%] items-center gap-3 self-start rounded-lg border border-gray-200 bg-white px-3 py-2.5">
			<div className="relative shrink-0">
				<div className="bg-primary/20 absolute inset-0 animate-ping rounded-full" />
				<div className="bg-primary/10 absolute inset-0 animate-pulse rounded-full" />
				<div className="from-primary/15 to-primary/5 relative flex size-8 items-center justify-center rounded-full bg-linear-to-br">
					<IconSparkles className="text-primary size-4" />
				</div>
			</div>
			<div className="flex flex-col gap-0.5">
				<span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
					{getStageLabel(currentProgress)}
					<span className="flex items-center gap-0.5">
						<span className="bg-primary size-1 animate-bounce rounded-full" />
						<span className="bg-primary size-1 animate-bounce rounded-full [animation-delay:150ms]" />
						<span className="bg-primary size-1 animate-bounce rounded-full [animation-delay:300ms]" />
					</span>
				</span>
				<RotatingMessage stage={currentProgress.stage} />
			</div>
		</div>
	)
}

const RotatingMessage: FC<{ stage: WebsiteEditProgress["stage"] }> = ({ stage }) => {
	const messages = useMemo(() => STAGE_MESSAGES[stage], [stage])

	const [index, setIndex] = useState(0)
	const [isVisible, setIsVisible] = useState(true)

	useEffect(() => {
		setIndex(0)
		setIsVisible(true)
	}, [messages])

	useEffect(() => {
		if (messages.length <= 1) return

		let fade: ReturnType<typeof setTimeout> | undefined
		const rotate = setInterval(() => {
			setIsVisible(false)
			fade = setTimeout(() => {
				setIndex(previous => (previous + 1) % messages.length)
				setIsVisible(true)
			}, FADE_DURATION_MS)
		}, ROTATION_INTERVAL_MS)

		return () => {
			clearInterval(rotate)
			clearTimeout(fade)
		}
	}, [messages])

	return (
		<span
			className="min-h-4 text-xs text-gray-500 transition-opacity"
			style={{ opacity: isVisible ? 1 : 0, transitionDuration: `${FADE_DURATION_MS}ms` }}
		>
			{messages[index] ?? messages[0]}
		</span>
	)
}

export default EditProgress
